import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { Button } from "../ui/button.js";

import { HelpMarkdown } from "./markdown.js";

import type { PageGuide } from "../../help/page-guide.js";

interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_WIDTH = 320;

/**
 * First-run walkthrough. Spotlights each step's anchored panel (via
 * `data-help-anchor`) with four dimming panels around it — leaving the target
 * interactive — plus a brand ring and a tooltip card. Steps without an anchor
 * render a centred modal. Tokens only (no raw colours / box-shadow tricks), so
 * it satisfies the design-canon lint.
 */
export function OnboardingTour({
  guide,
  open,
  onClose,
  onComplete,
}: {
  guide: PageGuide;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}) {
  const steps = guide.steps;
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const step = steps[stepIndex];
  const isLast = stepIndex >= steps.length - 1;

  // Note: the tour is remounted (via `key`) each time it opens, so stepIndex
  // starts at 0 without a reset-in-effect.

  const measure = useCallback(() => {
    if (!open || !step || !step.anchorId) {
      setRect(null);
      return;
    }
    const el = document.querySelector(`[data-help-anchor="${step.anchorId}"]`);
    if (!el) {
      setRect(null); // anchor not mounted (e.g. graph still loading) → centre the step
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [open, step]);

  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- measuring the anchored DOM rect into state on layout is the canonical useLayoutEffect DOM-sync case
    measure();
  }, [measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onChange = () => measure();
    window.addEventListener("resize", onChange);
    window.addEventListener("scroll", onChange, true);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, true);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      } else if (event.key === "ArrowRight") {
        event.stopPropagation();
        if (isLast) onComplete();
        else setStepIndex((i) => i + 1);
      } else if (event.key === "ArrowLeft") {
        event.stopPropagation();
        setStepIndex((i) => Math.max(0, i - 1));
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, isLast, onClose, onComplete]);

  if (!open || !step) return null;

  const viewportH = typeof window === "undefined" ? 768 : window.innerHeight;
  const viewportW = typeof window === "undefined" ? 1024 : window.innerWidth;

  const cardStyle: React.CSSProperties = rect
    ? {
        top: Math.min(rect.top + rect.height + 12, viewportH - 220),
        left: Math.min(Math.max(rect.left, 8), viewportW - CARD_WIDTH - 8),
        width: CARD_WIDTH,
      }
    : {};

  return (
    <div
      className="fixed inset-0 z-(--z-modal)"
      role="dialog"
      aria-modal="true"
      aria-label={`Walkthrough: ${step.title}`}
    >
      {rect ? (
        <>
          {/* Four dimming panels leave the anchored target undimmed + clickable. */}
          <div
            className="absolute bg-fg/50"
            style={{ top: 0, left: 0, right: 0, height: rect.top }}
          />
          <div
            className="absolute bg-fg/50"
            style={{ top: rect.top + rect.height, left: 0, right: 0, bottom: 0 }}
          />
          <div
            className="absolute bg-fg/50"
            style={{ top: rect.top, left: 0, width: rect.left, height: rect.height }}
          />
          <div
            className="absolute bg-fg/50"
            style={{ top: rect.top, left: rect.left + rect.width, right: 0, height: rect.height }}
          />
          <div
            className="pointer-events-none absolute rounded-md ring-2 ring-brand"
            style={{
              top: rect.top - 4,
              left: rect.left - 4,
              width: rect.width + 8,
              height: rect.height + 8,
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-fg/50" />
      )}

      <div
        className={
          rect
            ? "absolute rounded-md border bg-surface p-4 shadow-xl"
            : "absolute left-1/2 top-1/2 w-80 -translate-x-1/2 -translate-y-1/2 rounded-md border bg-surface p-4 shadow-xl"
        }
        style={rect ? cardStyle : undefined}
      >
        <p className="text-xs font-medium text-fg-subtle">
          Step {stepIndex + 1} of {steps.length}
        </p>
        <h2 className="mt-1 text-base font-semibold text-fg">{step.title}</h2>
        <HelpMarkdown content={step.body} className="mt-2 space-y-1 text-sm text-fg-muted" />
        <div className="mt-4 flex items-center justify-between gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Skip
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              disabled={stepIndex === 0}
            >
              Back
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => (isLast ? onComplete() : setStepIndex((i) => i + 1))}
            >
              {isLast ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
